/**
 * SignatureField Component - Digital signature capture
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Edit3, RotateCcw, Check } from 'lucide-react';
import { SignatureFieldProps } from '../../../types/forms';
import { BaseField } from './BaseField';

export const SignatureField: React.FC<SignatureFieldProps> = ({
  name,
  label,
  helpText,
  required = false,
  disabled = false,
  readonly = false,
  error,
  className = '',
  value,
  width = 400,
  height = 200,
  backgroundColor = '#ffffff',
  penColor = '#000000',
  onChange,
  onClear,
}) => {
  const fieldId = `field-${name}`;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = width;
    canvas.height = height;

    // Set background
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);

    // Load existing signature if value exists
    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        setHasSignature(true);
      };
      img.src = value;
    }
  }, [width, height, backgroundColor, value]);

  // Get mouse/touch position relative to canvas
  const getPosition = useCallback(
    (e: MouseEvent | TouchEvent): { x: number; y: number } | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      if ('touches' in e) {
        return {
          x: (e.touches[0].clientX - rect.left) * scaleX,
          y: (e.touches[0].clientY - rect.top) * scaleY,
        };
      } else {
        return {
          x: (e.clientX - rect.left) * scaleX,
          y: (e.clientY - rect.top) * scaleY,
        };
      }
    },
    []
  );

  // Start drawing
  const startDrawing = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (disabled || readonly) return;

      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;

      setIsDrawing(true);
      setHasSignature(true);

      const pos = getPosition(e.nativeEvent as MouseEvent | TouchEvent);
      if (pos) {
        ctx.beginPath();
        ctx.moveTo(pos.x, pos.y);
        ctx.strokeStyle = penColor;
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }

      e.preventDefault();
    },
    [disabled, readonly, penColor, getPosition]
  );

  // Draw on canvas
  const draw = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDrawing || disabled || readonly) return;

      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;

      const pos = getPosition(e.nativeEvent as MouseEvent | TouchEvent);
      if (pos) {
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
      }

      e.preventDefault();
    },
    [isDrawing, disabled, readonly, getPosition]
  );

  // Stop drawing
  const stopDrawing = useCallback(() => {
    if (isDrawing) {
      setIsDrawing(false);

      // Save signature as base64
      const canvas = canvasRef.current;
      if (canvas && hasSignature) {
        const dataURL = canvas.toDataURL('image/png');
        onChange?.(dataURL);
      }
    }
  }, [isDrawing, hasSignature, onChange]);

  // Clear signature
  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);
    setHasSignature(false);
    onChange?.(null);
    onClear?.();
  }, [width, height, backgroundColor, onChange, onClear]);

  const canvasClasses = `
    border-2 rounded-md
    ${error ? 'border-red-300' : 'border-gray-300'}
    ${disabled || readonly ? 'opacity-50 cursor-not-allowed' : 'cursor-crosshair'}
    ${!disabled && !readonly ? 'hover:border-blue-400' : ''}
    transition-colors duration-200
  `;

  return (
    <BaseField
      name={name}
      label={label}
      helpText={helpText}
      required={required}
      disabled={disabled}
      readonly={readonly}
      error={error}
      className={className}
      fieldId={fieldId}
    >
      <div className="space-y-2">
        <div className="relative inline-block">
          <canvas
            ref={canvasRef}
            id={fieldId}
            className={canvasClasses}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            style={{ touchAction: 'none' }}
          />
          
          {/* Signature line */}
          <div className="absolute bottom-8 left-4 right-4 border-b border-gray-400" />
          <div className="absolute bottom-2 left-4 text-xs text-gray-500">
            {hasSignature ? 'Signed' : 'Sign here'}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={clearSignature}
            disabled={disabled || readonly || !hasSignature}
            className="px-3 py-1 text-sm flex items-center gap-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCcw className="w-4 h-4" />
            Clear
          </button>
          
          {hasSignature && (
            <span className="px-3 py-1 text-sm flex items-center gap-1 text-green-600">
              <Check className="w-4 h-4" />
              Signature captured
            </span>
          )}
        </div>

        {/* Instructions */}
        {!disabled && !readonly && !hasSignature && (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Edit3 className="w-3 h-3" />
            Draw your signature using mouse or touch
          </div>
        )}
      </div>
    </BaseField>
  );
};

export default SignatureField;